import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	FileIcon,
	Label,
	RadioGroup,
	RadioGroupItem,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { formatSimpleDate } from "@dune/utils";
import { FileSignature, Mail } from "lucide-react";
import React, { useState, useEffect, useId } from "react";
import { toast } from "sonner";
import { useSendConvention } from "../../../../../api/mutations/documents.mutations";
import { useGetSocietyFiles } from "../../../../../api/queries/societies.queries";
import { SignatureMethodValues } from "@dune/types";

interface SendConventionModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	societyId: number;
	onSuccess?: () => void;
}

interface ConventionFile {
	id: string;
	fileId: string;
	fileName: string;
	createdAt: string;
	mimeType: string;
}

const SendConventionModal: React.FC<SendConventionModalProps> = ({
	open,
	onOpenChange,
	societyId,
	onSuccess,
}) => {
	const [selectedFileId, setSelectedFileId] = useState<string>("");
	const [signatureMethod, setSignatureMethod] =
		useState<SignatureMethodValues>("ELECTRONIC");
	const [selectOpen, setSelectOpen] = useState(false);
	const signatureRadioId = useId();

	const { data: files, isLoading: isLoadingFiles } = useGetSocietyFiles(
		societyId,
		"CONVENTION",
	);
	const sendConventionMutation = useSendConvention();

	const conventions: ConventionFile[] = (files || []).map((fileOnSociety) => ({
		id: fileOnSociety.id.toString(),
		fileId: fileOnSociety.file.id,
		fileName: fileOnSociety.file.name,
		createdAt: fileOnSociety.createdAt,
		mimeType: fileOnSociety.file.mimeType,
	}));

	const selectedConvention = conventions.find(
		(c) => c.fileId === selectedFileId,
	);

	useEffect(() => {
		if (conventions.length > 0 && !selectedFileId) {
			setSelectedFileId(conventions[0].fileId);
		}
	}, [conventions, selectedFileId]);

	useEffect(() => {
		if (!open) {
			setSelectOpen(false);
			setSelectedFileId("");
			setSignatureMethod("ELECTRONIC");
		}
	}, [open]);

	const handleSendConvention = () => {
		if (!selectedFileId) {
			toast.error("Veuillez sélectionner une convention");
			return;
		}

		sendConventionMutation.mutate(
			{
				societyId,
				signatureMethod,
				fileId: selectedFileId,
			},
			{
				onSuccess: () => {
					onOpenChange(false);
					onSuccess?.();
				},
			},
		);
	};

	const handleFileSelection = (value: string) => {
		setSelectedFileId(value);
		setTimeout(() => setSelectOpen(false), 50);
	};

	const isReady = !isLoadingFiles && conventions.length > 0 && selectedFileId;
	const isLoading = sendConventionMutation.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Envoyer la convention</DialogTitle>
					<DialogDescription>
						Sélectionnez la convention et le mode de signature
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<label htmlFor="convention-select" className="text-sm font-medium">
							Convention à envoyer
						</label>
						{isLoadingFiles ? (
							<div className="p-2 text-sm text-gray-500">Chargement...</div>
						) : conventions.length === 0 ? (
							<div className="p-3 text-sm text-gray-500 bg-gray-50 rounded-md">
								Aucune convention disponible. Veuillez d'abord générer une
								convention.
							</div>
						) : (
							<Select
								value={selectedFileId}
								onValueChange={handleFileSelection}
								disabled={isLoading}
								open={selectOpen}
								onOpenChange={setSelectOpen}
							>
								<SelectTrigger id="convention-select">
									<SelectValue>
										{selectedConvention && (
											<div className="flex items-center gap-2">
												<FileIcon
													mimeType={selectedConvention.mimeType}
													className="w-4 h-4"
												/>
												<span>{selectedConvention.fileName}</span>
											</div>
										)}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{conventions.map((convention) => (
										<SelectItem
											key={convention.fileId}
											value={convention.fileId}
										>
											<div className="flex items-center gap-2">
												<FileIcon
													mimeType={convention.mimeType}
													className="w-4 h-4"
												/>
												<div>
													<div className="font-medium">
														{convention.fileName}
													</div>
													<div className="text-xs text-gray-500">
														{formatSimpleDate(convention.createdAt)}
													</div>
												</div>
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>

					{isReady && (
						<div className="border-t pt-4">
							<fieldset className="space-y-2">
								<legend className="text-sm font-medium">
									Mode de signature
								</legend>
								<RadioGroup
									value={signatureMethod}
									onValueChange={(value) =>
										setSignatureMethod(value as SignatureMethodValues)
									}
									className="grid grid-cols-2 gap-3"
								>
									<Label
										htmlFor={`${signatureRadioId}-ELECTRONIC`}
										className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
									>
										<RadioGroupItem
											id={`${signatureRadioId}-ELECTRONIC`}
											value="ELECTRONIC"
											className="absolute top-2 right-2"
										/>
										<Mail className="h-8 w-8 mb-1 text-blue-600" />
										<span className="font-medium text-center">
											Signature électronique
										</span>
										<span className="text-xs text-muted-foreground text-center">
											Envoi par email avec DocuSign
										</span>
									</Label>

									<Label
										htmlFor={`${signatureRadioId}-MANUAL`}
										className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
									>
										<RadioGroupItem
											id={`${signatureRadioId}-MANUAL`}
											value="MANUAL"
											className="absolute top-2 right-2"
										/>
										<FileSignature className="h-8 w-8 mb-1 text-green-600" />
										<span className="font-medium text-center">
											Signature manuelle
										</span>
										<span className="text-xs text-muted-foreground text-center">
											Créer une tâche pour signature papier
										</span>
									</Label>
								</RadioGroup>
							</fieldset>
						</div>
					)}

					{selectedConvention?.mimeType.includes("wordprocessingml") &&
						signatureMethod === "ELECTRONIC" && (
							<div className="rounded-lg bg-blue-50 p-3">
								<p className="text-sm text-blue-800">
									⚠️ Ce fichier Word sera automatiquement converti en PDF pour la
									signature électronique.
								</p>
							</div>
						)}
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isLoading}
					>
						Annuler
					</Button>
					{isReady && (
						<Button
							onClick={handleSendConvention}
							disabled={isLoading}
							isLoading={isLoading}
						>
							Envoyer pour signature
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default SendConventionModal;
